import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Download, ShieldCheck, FileSignature, MapPin, RefreshCw, Loader2, ExternalLink } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API } from "../../App";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";

/**
 * ComplianceTab — ESIGN/UETA-friendly signature audit view.
 *
 * Renders three sections:
 *   1. Summary tiles (total signed, current-month signed, per-doc-type split)
 *   2. Monthly bar chart of signed docs (last 12 months, stacked invoice/estimate)
 *   3. Audit-log table (signer, email, IP, timestamp) + CSV export
 *
 * Multi-location tenants get a location dropdown that filters both the chart
 * and the table (backend supports ?location_id=...). Single-location tenants
 * see the dropdown collapsed to just "All locations".
 */
const ComplianceTab = ({ user, headers }) => {
  const { isDark } = useTheme();
  const isStore = user?.account_type === "multi_location_store";

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const PAGE = 20;

  // ── Load store locations for the filter dropdown ──
  useEffect(() => {
    if (!isStore) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/store/locations`, { headers });
        // Endpoint returns { locations: [...] } (see backend/routers/store.py:97).
        // Older callers may have returned a bare array — accept either shape.
        const list = Array.isArray(r.data) ? r.data : (r.data?.locations || []);
        setLocations(list);
      } catch { /* non-fatal */ }
    })();
  }, [isStore, headers]);

  const loadData = useCallback(async (opts = {}) => {
    const nextSkip = opts.skip ?? 0;
    setLoading(true);
    try {
      const params = { limit: PAGE, skip: nextSkip };
      if (locationId) params.location_id = locationId;
      const [sRes, aRes] = await Promise.all([
        axios.get(`${API}/invoices/compliance/monthly-stats`, {
          headers,
          params: { months: 12, ...(locationId ? { location_id: locationId } : {}) },
        }),
        axios.get(`${API}/invoices/compliance/audit-log`, { headers, params }),
      ]);
      setStats(sRes.data);
      // Append if paging forward, else replace.
      setItems(prev => (nextSkip > 0 ? [...prev, ...(aRes.data.items || [])] : (aRes.data.items || [])));
      setTotal(aRes.data.total || 0);
      setHasMore(!!aRes.data.has_more);
      setSkip(nextSkip);
    } catch (e) {
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  }, [headers, locationId]);

  useEffect(() => { loadData({ skip: 0 }); /* refetch on location change */ }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = locationId ? { location_id: locationId } : {};
      const r = await axios.get(`${API}/invoices/compliance/audit-log.csv`, {
        headers,
        params,
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `esign-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // ── Derived summary numbers ──
  const summary = useMemo(() => {
    const months = stats?.months || [];
    const thisMonth = months[months.length - 1] || { total: 0, invoice: 0, estimate: 0 };
    const totalInvoices = months.reduce((s, m) => s + (m.invoice || 0), 0);
    const totalEstimates = months.reduce((s, m) => s + (m.estimate || 0), 0);
    return {
      total: stats?.total_signed || 0,
      thisMonth: thisMonth.total,
      invoices: totalInvoices,
      estimates: totalEstimates,
    };
  }, [stats]);

  const chartData = useMemo(() => {
    return (stats?.months || []).map(m => ({
      // Show a short month label (e.g. "Jul '25")
      label: (() => {
        const [y, mm] = m.month.split("-");
        const d = new Date(Number(y), Number(mm) - 1, 1);
        return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      })(),
      invoice: m.invoice,
      estimate: m.estimate,
    }));
  }, [stats]);

  // ── UI ──
  return (
    <div className="space-y-6" data-testid="compliance-tab">
      {/* Header + description */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-500" />
            <h3 className="text-lg font-bold text-foreground font-['Manrope']">E-Signature Compliance</h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            ESIGN/UETA-friendly audit trail for every invoice and estimate signed by a customer.
            Track monthly volume and export the full audit log (signer, email, IP, timestamp) for auditors.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isStore && (
            <Select value={locationId || "__all"} onValueChange={(v) => setLocationId(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="compliance-location-filter">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadData({ skip: 0 })}
            disabled={loading}
            data-testid="compliance-refresh-btn"
            className="gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || total === 0}
            size="sm"
            className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/25 gap-1.5"
            data-testid="compliance-export-csv-btn"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="Signed Documents" value={summary.total} sub="all time (12 mo)" accent="emerald" testid="stat-total-signed" />
        <StatTile label="This Month" value={summary.thisMonth} sub={new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })} accent="blue" testid="stat-month-signed" />
        <StatTile label="Signed Invoices" value={summary.invoices} sub="last 12 months" accent="blue" testid="stat-invoices-signed" />
        <StatTile label="Signed Estimates" value={summary.estimates} sub="last 12 months" accent="orange" testid="stat-estimates-signed" />
      </div>

      {/* Monthly chart */}
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-emerald-500" /> Monthly Signature Volume
          </CardTitle>
        </CardHeader>
        <CardContent className="pl-2 pr-4 pb-4">
          {loading && !stats ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading chart…
            </div>
          ) : chartData.every(d => d.invoice === 0 && d.estimate === 0) ? (
            <div className="h-64 flex flex-col items-center justify-center text-muted-foreground text-sm text-center px-4" data-testid="chart-empty">
              <FileSignature className="w-8 h-8 mb-2 opacity-40" />
              No signed documents yet.<br />
              Once your customers sign invoices or estimates, monthly volume will appear here.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#27272a" : "#e5e7eb"} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: isDark ? "#a1a1aa" : "#6b7280" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: isDark ? "#a1a1aa" : "#6b7280" }} tickLine={false} axisLine={false} width={30} />
                <Tooltip
                  contentStyle={{
                    background: isDark ? "#18181b" : "#ffffff",
                    border: `1px solid ${isDark ? "#27272a" : "#e5e7eb"}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="invoice" stackId="a" fill="#3b82f6" name="Invoices" radius={[0, 0, 0, 0]} />
                <Bar dataKey="estimate" stackId="a" fill="#f97316" name="Estimates" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Audit log table */}
      <Card className="glass-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-blue-500" /> Signature Audit Log
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {total} record{total === 1 ? "" : "s"}
          </Badge>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="compliance-audit-table">
              <thead>
                <tr className={`border-b ${isDark ? "border-zinc-800" : "border-gray-200"}`}>
                  <Th>Doc</Th>
                  <Th>Signer</Th>
                  <Th>Email</Th>
                  <Th className="hidden md:table-cell">IP</Th>
                  <Th>Signed At</Th>
                  {isStore && <Th className="hidden lg:table-cell">Location</Th>}
                  <Th className="text-right">Amount</Th>
                  <Th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr>
                    <td colSpan={isStore ? 8 : 7} className="py-10 text-center text-sm text-muted-foreground">
                      No signed documents{locationId ? " for this location" : ""} yet.
                    </td>
                  </tr>
                )}
                {items.map((r) => (
                  <tr key={r.invoice_id} className={`border-b last:border-0 hover:bg-muted/30 ${isDark ? "border-zinc-800" : "border-gray-100"}`} data-testid={`audit-row-${r.invoice_id}`}>
                    <Td>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${r.document_type === "estimate" ? "border-orange-500/40 text-orange-500" : "border-blue-500/40 text-blue-500"}`}>
                          {r.document_type === "estimate" ? "EST" : "INV"}
                        </Badge>
                        <span className="font-medium">{r.invoice_number || r.invoice_id.slice(0, 8)}</span>
                      </div>
                      {r.customer_name && <div className="text-xs text-muted-foreground mt-0.5">{r.customer_name}</div>}
                    </Td>
                    <Td className="font-medium">{r.signature_signer_name || "—"}</Td>
                    <Td className="text-muted-foreground truncate max-w-[220px]">{r.signature_signer_email || "—"}</Td>
                    <Td className="hidden md:table-cell text-muted-foreground text-xs font-mono">{r.signature_ip || "—"}</Td>
                    <Td className="text-xs text-muted-foreground whitespace-nowrap">
                      {r.signature_signed_at ? new Date(r.signature_signed_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    </Td>
                    {isStore && <Td className="hidden lg:table-cell text-muted-foreground text-xs">{r.location_name || "—"}</Td>}
                    <Td className="text-right font-semibold whitespace-nowrap">${(r.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                    <Td>
                      {r.public_token && (
                        <a
                          href={`${window.location.origin}/${r.document_type === "estimate" ? "estimate" : "invoice"}/${r.public_token}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-muted-foreground hover:text-foreground inline-flex items-center"
                          title="Open signed document"
                          data-testid={`audit-view-${r.invoice_id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="p-4 flex justify-center border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadData({ skip: skip + PAGE })}
                disabled={loading}
                data-testid="compliance-load-more-btn"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Load more
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compliance footer */}
      <p className="text-xs text-muted-foreground leading-relaxed max-w-3xl">
        Every signature captures the signer&apos;s full name and email address (typed), a canvas-drawn signature image,
        the customer&apos;s IP address, browser user-agent, and a UTC timestamp. Signed documents are locked
        from further owner-side edits to preserve the audit trail. Compliant with the U.S. E-Sign Act and
        UETA in states that have adopted it.
      </p>
    </div>
  );
};

// ─── Presentation helpers ──────────────────────────────────────

const ACCENTS = {
  emerald: "text-emerald-500 bg-emerald-500/10",
  blue: "text-blue-500 bg-blue-500/10",
  orange: "text-orange-500 bg-orange-500/10",
};

const StatTile = ({ label, value, sub, accent = "emerald", testid }) => (
  <div className="glass-card rounded-xl p-4" data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{label}</p>
    <div className="flex items-baseline gap-2 mt-1">
      <span className={`text-2xl font-bold text-foreground font-['Manrope']`}>{value.toLocaleString()}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${ACCENTS[accent] || ACCENTS.emerald}`}>signed</span>
    </div>
    <p className="text-[11px] text-muted-foreground/60 mt-1">{sub}</p>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 py-2.5 ${className}`}>
    {children}
  </th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-sm text-foreground align-middle ${className}`}>{children}</td>
);

export default ComplianceTab;
